import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import { RxCross2 } from 'react-icons/rx';
import { CircularProgress } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getAIUsageStatus,
  generateAISurvey,
  createAISurveys,
} from '../surveyActions';
import { useGlobalContext } from '../../../Contexts/GlobalContext';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 720,
  maxWidth: '95vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
  borderRadius: 2,
  overflow: 'hidden',
};

const AISurveyModal = ({ open, onClose, user, organization, onSuccess }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSnackbar, teamMembers } = useGlobalContext();

  const [step, setStep] = useState(1); // 1: RFP Input, 2: Preview
  const [rfpText, setRfpText] = useState('');
  const [generatedSurveys, setGeneratedSurveys] = useState([]);
  const [isMultiCountry, setIsMultiCountry] = useState(false);
  const [suggestedGroupName, setSuggestedGroupName] = useState('');
  const [createGroup, setCreateGroup] = useState(true);
  const [groupName, setGroupName] = useState('');
  const [selectedProjectManager, setSelectedProjectManager] = useState(user?._id || '');

  // Fetch AI usage status
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryFn: () => getAIUsageStatus(user?.organizationId),
    queryKey: ['aiUsageStatus', user?.organizationId],
    enabled: !!user?.organizationId && open,
    staleTime: 30000,
  });

  // Generate survey mutation
  const generateMutation = useMutation({
    mutationFn: () => generateAISurvey(rfpText, user?.organizationId),
    onSuccess: (data) => {
      setGeneratedSurveys(data.surveys || []);
      setIsMultiCountry(data.isMultiCountry || false);
      setSuggestedGroupName(data.suggestedGroupName || '');
      setGroupName(data.suggestedGroupName || '');
      setStep(2);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        msg: error.response?.data?.error || error.message || 'Failed to generate survey',
        severity: 'error',
      });
    },
  });

  // Create surveys mutation
  const createMutation = useMutation({
    mutationFn: () =>
      createAISurveys(
        generatedSurveys,
        user?.organizationId,
        user?._id,
        user?.organizationName || organization?.name,
        selectedProjectManager,
        isMultiCountry && createGroup,
        groupName
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        msg: `Successfully created ${data.createdCount} survey(s)!`,
        severity: 'success',
      });
      queryClient.invalidateQueries(['aiUsageStatus']);
      queryClient.invalidateQueries(['allSurveyStatus']);
      queryClient.invalidateQueries(['surveyCounts']);
      handleClose();
      onSuccess?.();
      
      // Navigate to the survey or group
      if (data.surveyGroupId) {
        navigate(`/survey-groups/${data.surveyGroupId}`);
      } else if (data.surveys?.length === 1) {
        navigate(`/surveys/${data.surveys[0]._id}/details`);
      }
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        msg: error.response?.data?.error || error.message || 'Failed to create surveys',
        severity: 'error',
      });
    },
  });

  const handleClose = () => {
    setStep(1);
    setRfpText('');
    setGeneratedSurveys([]);
    setIsMultiCountry(false);
    setSuggestedGroupName('');
    setGroupName('');
    setCreateGroup(true);
    onClose();
  };

  const handleGenerate = () => {
    if (rfpText.trim().length < 50) {
      setSnackbar({
        open: true,
        msg: 'Please provide more details in your RFP (at least 50 characters)',
        severity: 'warning',
      });
      return;
    }
    generateMutation.mutate();
  };

  const handleCreate = () => {
    if (!selectedProjectManager) {
      setSnackbar({
        open: true,
        msg: 'Please select a project manager',
        severity: 'warning',
      });
      return;
    }
    createMutation.mutate();
  };

  const handleSurveyChange = (index, field, value) => {
    setGeneratedSurveys((prev) =>
      prev.map((survey, i) =>
        i === index ? { ...survey, [field]: value } : survey
      )
    );
  };

  const handleRemoveSurvey = (index) => {
    if (generatedSurveys.length > 1) {
      setGeneratedSurveys((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const remainingQuota = usageData?.remaining ?? 25;
  const isOverQuota = generatedSurveys.length > remainingQuota;

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={style}>
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Header - Clean white */}
            <div className="px-6 py-5 border-b border-[#E0E0E0] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0226BE] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-[17px] font-[600] text-[#333]">
                    {step === 1 ? 'Create Survey with AI' : 'Review Generated Surveys'}
                  </h2>
                  <p className="text-[13px] text-[#666]">
                    {step === 1
                      ? 'Paste your RFP and let AI configure your survey'
                      : `${generatedSurveys.length} survey(s) generated from your RFP`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {!usageLoading && (
                  <div className="text-right px-3 py-1.5 bg-[#F5F5F5] rounded-lg">
                    <p className="text-[11px] text-[#888]">Daily Quota</p>
                    <p className="text-[13px] font-[600] text-[#0226BE]">
                      {remainingQuota}/25 remaining
                    </p>
                  </div>
                )}
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-[#F5F5F5] rounded-lg transition-colors"
                >
                  <RxCross2 size={18} className="text-[#666]" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-[#FAFAFA]">
              {step === 1 ? (
                /* Step 1: RFP Input */
                <div className="space-y-5">
                  <div className="bg-white rounded-lg border border-[#E0E0E0] p-5">
                    <label className="block text-[13px] font-[500] text-[#333] mb-3">
                      Paste your RFP or Project Brief
                    </label>
                    <textarea
                      value={rfpText}
                      onChange={(e) => setRfpText(e.target.value)}
                      placeholder={`Example RFP:

Project: Brand Awareness Study
Markets: United States, United Kingdom, Germany
Target Audience: General consumers aged 18-65
Sample Size: 500 per country
Survey Length: 15 minutes
Incidence Rate: ~60%
Study Type: Ad hoc brand tracking

Include details about study objectives, target demographics, countries, sample size, and survey length...`}
                      className="w-full h-[260px] p-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0226BE] focus:border-[#0226BE] text-[14px] text-[#333] placeholder:text-[#999] resize-none bg-[#FAFAFA]"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-[12px] text-[#888]">
                        {rfpText.length} characters (minimum 50 required)
                      </p>
                      {rfpText.length >= 50 && (
                        <p className="text-[12px] text-green-600 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Ready to generate
                        </p>
                      )}
                    </div>
                  </div>

                  {/* What AI extracts - simple list */}
                  <div className="bg-white rounded-lg border border-[#E0E0E0] p-5">
                    <h4 className="text-[13px] font-[500] text-[#333] mb-3">
                      AI will automatically extract:
                    </h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      {[
                        'Survey name & description',
                        'Target countries',
                        'Target audience (B2B/B2C)',
                        'Sample size & incidence rate',
                        'Survey length (LOI)',
                        'Suggested CPI pricing',
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-[13px] text-[#555]">
                          <svg className="w-4 h-4 text-[#0226BE]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* Step 2: Preview & Edit */
                <div className="space-y-4">
                  {/* Project Manager Selection */}
                  <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
                    <label className="block text-[13px] font-[500] text-[#333] mb-2">
                      Project Manager <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={selectedProjectManager}
                      onChange={(e) => setSelectedProjectManager(e.target.value)}
                      className="w-full h-[42px] px-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0226BE] text-[14px] text-[#333] bg-white"
                    >
                      <option value="">Select Project Manager</option>
                      {teamMembers?.map((member) => (
                        <option key={member._id} value={member._id}>
                          {member.personalDetails?.fullName || member.email}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Multi-country group option */}
                  {isMultiCountry && (
                    <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-[13px] font-[500] text-[#333]">
                            Multi-Country Project
                          </h4>
                          <p className="text-[12px] text-[#666]">
                            {generatedSurveys.length} countries detected
                          </p>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={createGroup}
                            onChange={(e) => setCreateGroup(e.target.checked)}
                            className="w-4 h-4 rounded text-[#0226BE] focus:ring-[#0226BE] border-[#D0D0D0]"
                          />
                          <span className="text-[13px] text-[#555]">Create as Survey Group</span>
                        </label>
                      </div>
                      {createGroup && (
                        <input
                          type="text"
                          value={groupName}
                          onChange={(e) => setGroupName(e.target.value)}
                          placeholder="Enter group name"
                          className="w-full h-[40px] px-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0226BE] text-[14px] bg-white"
                        />
                      )}
                    </div>
                  )}

                  {/* Survey Cards */}
                  <div className="space-y-3">
                    {generatedSurveys.map((survey, index) => (
                      <div
                        key={index}
                        className="bg-white border border-[#E0E0E0] rounded-lg p-4 hover:border-[#0226BE]/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-[12px] font-[600] ${
                              survey.targetAudience === 'B2B' ? 'bg-orange-500' : 'bg-[#0226BE]'
                            }`}>
                              {survey.countryCode}
                            </div>
                            <div>
                              <input
                                type="text"
                                value={survey.surveyName}
                                onChange={(e) => handleSurveyChange(index, 'surveyName', e.target.value)}
                                className="text-[14px] font-[500] text-[#333] border-b border-transparent hover:border-[#E0E0E0] focus:border-[#0226BE] focus:outline-none bg-transparent w-full"
                              />
                              <p className="text-[12px] text-[#888]">
                                {survey.country} · {survey.targetAudience}
                              </p>
                            </div>
                          </div>
                          {generatedSurveys.length > 1 && (
                            <button
                              onClick={() => handleRemoveSurvey(index)}
                              className="p-1.5 hover:bg-[#FEE2E2] rounded text-[#999] hover:text-red-500 transition-colors"
                            >
                              <RxCross2 size={14} />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <label className="block text-[11px] text-[#888] mb-1">Completes</label>
                            <input
                              type="number"
                              value={survey.expectedCompletes}
                              onChange={(e) => handleSurveyChange(index, 'expectedCompletes', Number(e.target.value))}
                              min={10}
                              max={1000000}
                              className="w-full h-[36px] px-3 border border-[#E0E0E0] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0226BE]"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-[#888] mb-1">LOI (min)</label>
                            <input
                              type="number"
                              value={survey.expectedCompletionLOI}
                              onChange={(e) => handleSurveyChange(index, 'expectedCompletionLOI', Number(e.target.value))}
                              min={1}
                              max={90}
                              step={0.5}
                              className="w-full h-[36px] px-3 border border-[#E0E0E0] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0226BE]"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-[#888] mb-1">IR (%)</label>
                            <input
                              type="number"
                              value={survey.expectedIncidenceRate}
                              onChange={(e) => handleSurveyChange(index, 'expectedIncidenceRate', Number(e.target.value))}
                              min={1}
                              max={100}
                              className="w-full h-[36px] px-3 border border-[#E0E0E0] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0226BE]"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] text-[#888] mb-1">CPI ($)</label>
                            <input
                              type="number"
                              value={survey.surveyCpi}
                              onChange={(e) => handleSurveyChange(index, 'surveyCpi', Number(e.target.value))}
                              min={1}
                              max={499}
                              step={0.5}
                              className="w-full h-[36px] px-3 border border-[#E0E0E0] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0226BE]"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {isOverQuota && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-[13px] text-red-700 font-[500]">
                        You're trying to create {generatedSurveys.length} surveys but only have {remainingQuota} remaining in your daily quota.
                      </p>
                      <p className="text-[12px] text-red-600 mt-1">
                        Please remove some surveys or try again tomorrow.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer - Clean */}
            <div className="px-6 py-4 border-t border-[#E0E0E0] flex items-center justify-between bg-white">
              {step === 2 ? (
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-[13px] font-[500] text-[#555] hover:text-[#333] hover:bg-[#F5F5F5] rounded-lg transition-colors"
                >
                  ← Back to RFP
                </button>
              ) : (
                <div />
              )}
              
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2.5 text-[13px] font-[500] text-[#555] hover:bg-[#F5F5F5] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                
                {step === 1 ? (
                  <button
                    onClick={handleGenerate}
                    disabled={rfpText.trim().length < 50 || generateMutation.isPending || remainingQuota <= 0}
                    className="px-5 py-2.5 text-[13px] font-[500] text-white bg-[#0226BE] hover:bg-[#021ea8] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {generateMutation.isPending ? (
                      <>
                        <CircularProgress size={14} color="inherit" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                        Generate Preview
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleCreate}
                    disabled={isOverQuota || createMutation.isPending || !selectedProjectManager}
                    className="px-5 py-2.5 text-[13px] font-[500] text-white bg-[#0226BE] hover:bg-[#021ea8] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {createMutation.isPending ? (
                      <>
                        <CircularProgress size={14} color="inherit" />
                        Creating...
                      </>
                    ) : (
                      <>
                        Create {generatedSurveys.length} Survey{generatedSurveys.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AISurveyModal;
